import React, { useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Col, Container, Row } from "react-bootstrap";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import NavBar from "../../../Common/NavBar";
import { ProtocolosURL } from "../../../API/apiurls";

export function ProtocolosDispositivo() {
  const navigation = useNavigate();
  const [protocolos, setProtocolos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedTransports, setSelectedTransports] = useState({});

  useEffect(() => {
    const empresaId = Number(localStorage.getItem("empresaid"));
    if (empresaId !== 1) {
      navigation("/", { replace: true });
      return;
    }

    Swal.fire({
      title: "Panel sensible",
      text: "Los protocolos MT-1, MT-2 y MT-2-JSON son legados y deben preservarse tal como están.",
      icon: "info",
      confirmButtonText: "Entendido",
    });
  }, [navigation]);

  useEffect(() => {
    let active = true;

    const cargarProtocolos = async () => {
      try {
        setLoading(true);
        setErrorMessage("");
        const response = await axios.get(ProtocolosURL);
        if (active) {
          setProtocolos(response.data ?? []);
        }
      } catch (error) {
        if (active) {
          setErrorMessage("No fue posible cargar el catálogo persistido de protocolos.");
          setProtocolos([]);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    cargarProtocolos();

    return () => {
      active = false;
    };
  }, []);

  const resumen = [
    { label: "Protocolos persistidos", value: String(protocolos.length) },
    {
      label: "Formatos vigentes",
      value: [
        ...new Set(
          protocolos.map((protocolo) => (protocolo.modo?.includes("JSON") ? "JSON" : "TXT"))
        ),
      ].join(" / ") || "-",
    },
    {
      label: "Transportes objetivo",
      value:
        [
          ...new Set(
            protocolos.flatMap((protocolo) => (protocolo.transporte ?? []).map((transport) => transport))
          ),
        ].join(" / ") || "-",
    },
  ];

  const normalizeCode = (value) =>
    String(value || "")
      .trim()
      .toUpperCase()
      .replace(/\s+/g, "")
      .replace(/_/g, "-");

  const isMqttCapableProtocol = (protocolo) => {
    const code = normalizeCode(protocolo?.codigo);
    return code === "MT-3" || code === "MT-03" || code === "MT-3-JSON" || code === "MT-03-JSON";
  };

  const isSelectedTransportMqtt = (protocolo) => {
    const selectedTransport = selectedTransports[protocolo?.codigo] ?? protocolo?.transporte?.[0] ?? "";
    return String(selectedTransport || "").toUpperCase() === "MQTT";
  };

  const hideObservations = (protocolo) => {
    const code = normalizeCode(protocolo?.codigo);
    return code === "MT-3" || code === "MT-03" || code === "MT-3-JSON" || code === "MT-03-JSON";
  };

  const buildMqttRequestTopic = (protocolo) => `protocolos/dis/{idDispositivo}/${normalizeCode(protocolo?.codigo).toLowerCase()}/solicitud`;
  const buildMqttResponseTopic = (protocolo) => `protocolos/dis/{idDispositivo}/${normalizeCode(protocolo?.codigo).toLowerCase()}`;
  const buildMqttRequestPayload = () => "solicitar_datos,2026-05-14 17:04:00";
  const buildMqttResponsePayload = (protocolo) =>
    protocolo?.modo?.includes("JSON")
      ? `{
  "coordenadas": "6.0070280, -75.6169290",
  "rdo": 250,
  "nsv": "30",
  "codv": 3,
  "cods": 180,
  "tiempoAudioVelocidad": 1200,
  "tiempoAudioGeocerca": 900,
  "valorBoton": 255
}`
      : `6.0070280, -75.6169290, 250, 30, 3, 180, 1200, 900, 255`;

  return (
    <div style={{ minHeight: "100vh", background: "#080404", color: "#e5e7eb" }}>
      <NavBar />

      <Container fluid style={{ paddingTop: 96, paddingBottom: 40, maxWidth: 1550 }}>
        <Row className="g-4">
          <Col xs={12}>
            <Card style={{ background: "#111827", border: "1px solid #334155" }}>
              <Card.Body>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                      <h1 style={{ margin: 0, fontSize: "2rem", color: "#f8fafc" }}>Protocolos de Dispositivo</h1>
                      <Badge bg="info" text="dark">SUPER_ADMIN</Badge>
                    </div>
                    <p style={{ margin: "10px 0 0", color: "#cbd5e1" }}>
                      Vista central para reconocer protocolos vigentes, sus campos, el modo de envío y su nivel de compatibilidad.
                    </p>
                  </div>

                  <Button variant="outline-light" onClick={() => navigation("/panel-administrador")}>
                    Volver a Administración
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12}>
            <Alert variant="warning" style={{ background: "#3f2f12", color: "#fde68a", border: "1px solid #92400e", marginBottom: 0 }}>
              No modificar protocolos existentes: <b>MT-1</b>, <b>MT-2</b> ni <b>MT-2-JSON</b>. Las ampliaciones deben salir en contratos nuevos.
            </Alert>
          </Col>

          {errorMessage ? (
            <Col xs={12}>
              <Alert variant="danger" style={{ marginBottom: 0 }}>
                {errorMessage}
              </Alert>
            </Col>
          ) : null}

          {resumen.map((item) => (
            <Col key={item.label} xs={12} sm={6} xl={3}>
              <Card style={{ background: "#111827", border: "1px solid #334155", height: "100%" }}>
                <Card.Body>
                  <div style={{ fontSize: 13, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                    {item.label}
                  </div>
                  <div style={{ fontSize: "1.8rem", fontWeight: 700, color: "#f8fafc", marginTop: 8 }}>
                    {item.value}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}

          <Col xs={12}>
            <Card style={{ background: "#111827", border: "1px solid #334155" }}>
              <Card.Body>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
                  <h3 style={{ color: "#f8fafc", margin: 0 }}>Matriz de protocolos</h3>
                </div>
                {loading ? (
                  <div style={{ color: "#cbd5e1", padding: "18px 0" }}>Cargando protocolos persistidos...</div>
                ) : null}
                <Row className="g-4">
                  {protocolos.map((protocolo) => {
                    const isExisting = protocolo.estado === "Existente";
                    const statusBg = isExisting ? "success" : "warning";
                    const cardBorder = isExisting ? "#2563eb" : "#f59e0b";
                    const selectedTransport = selectedTransports[protocolo.codigo] ?? protocolo.transporte[0] ?? "";
                    const endpointsVisibles = selectedTransport
                      ? protocolo.endpointUrls.filter((endpoint) => endpoint.transport === selectedTransport)
                      : protocolo.endpointUrls;
                    const chipStyle = {
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "4px 10px",
                      borderRadius: 999,
                      border: "1px solid rgba(255,255,255,0.12)",
                      background: "rgba(255,255,255,0.05)",
                      color: "#dbe4ff",
                      fontSize: 12,
                      marginRight: 8,
                      marginBottom: 8,
                    };

                    return (
                      <Col key={protocolo.codigo} xs={12} lg={6} xxl={4}>
                        <Card
                          style={{
                            height: "100%",
                            background: "linear-gradient(180deg, rgba(17,24,39,0.98) 0%, rgba(15,23,42,0.98) 100%)",
                            border: `1px solid ${cardBorder}`,
                            boxShadow: "0 18px 38px rgba(0,0,0,0.35)",
                          }}
                        >
                          <Card.Body style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
                              <div>
                                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                                  <Badge bg="light" text="dark" style={{ fontSize: 12 }}>
                                    {protocolo.codigo}
                                  </Badge>
                                  <Badge bg={statusBg} text={isExisting ? "dark" : "dark"}>
                                    {protocolo.estado}
                                  </Badge>
                                </div>
                                <h3 style={{ margin: "10px 0 0", color: "#f8fafc", fontWeight: 950, fontSize: "1.55rem", lineHeight: 1.05, letterSpacing: "-0.03em" }}>
                                  Protocolo {protocolo.codigo}
                                </h3>
                                <div style={{ marginTop: 8, color: "#eef2ff", fontSize: "1.05rem", lineHeight: 1.4, fontWeight: 600 }}>
                                  {protocolo.nombre}
                                </div>
                              </div>

                              <Badge
                                bg="dark"
                                style={{
                                  background: "rgba(255,255,255,0.08)",
                                  border: "1px solid rgba(255,255,255,0.10)",
                                  color: "#cbd5e1",
                                  fontSize: 11,
                                }}
                              >
                                {protocolo.modo.includes("JSON") ? "JSON" : "TXT"}
                              </Badge>
                            </div>

                            <div>
                              <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                                Campos
                              </div>
                              <div style={{ lineHeight: 1.7, color: "#e2e8f0" }}>{protocolo.campos}</div>
                            </div>

                            <div>
                              <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                                Orden de lectura
                              </div>
                              <div style={{ display: "flex", flexWrap: "wrap" }}>
                                {protocolo.orden.split(" ").map((item) => (
                                  <span key={`${protocolo.codigo}-${item}`} style={chipStyle}>
                                    {item}
                                  </span>
                                ))}
                              </div>
                            </div>

                            <div
                              style={{
                                padding: 12,
                                borderRadius: 14,
                                background: "rgba(255,255,255,0.04)",
                                border: "1px solid rgba(255,255,255,0.08)",
                              }}
                            >
                              <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                                Ejemplo
                              </div>
                              <div style={{ color: "#f8fafc", fontFamily: "monospace", fontSize: 13, wordBreak: "break-word" }}>
                                {protocolo.ejemplo}
                              </div>
                            </div>

                            <div>
                              <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                                Modo de envío
                              </div>
                              <div style={{ color: "#cbd5e1", lineHeight: 1.65 }}>{protocolo.modo}</div>
                              <div style={{ marginTop: 10 }}>
                                <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                                  Transportes soportados
                                </div>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                                  {protocolo.transporte.map((item) => (
                                    <button
                                      key={`${protocolo.codigo}-transport-${item}`}
                                      type="button"
                                      onClick={() =>
                                        setSelectedTransports((current) => ({
                                          ...current,
                                          [protocolo.codigo]: item,
                                        }))
                                      }
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        padding: "5px 12px",
                                        borderRadius: 999,
                                        background: selectedTransport === item ? "rgba(59,130,246,0.34)" : "rgba(13,110,253,0.18)",
                                        border: selectedTransport === item ? "1px solid rgba(191,219,254,0.7)" : "1px solid rgba(127,176,255,0.26)",
                                        color: "#dbeafe",
                                        fontSize: 12,
                                        fontWeight: 800,
                                        letterSpacing: "0.04em",
                                        cursor: "pointer",
                                        outline: "none",
                                      }}
                                    >
                                      {item}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>

                            {isMqttCapableProtocol(protocolo) && isSelectedTransportMqtt(protocolo) ? (
                              <div
                                style={{
                                  padding: 12,
                                  borderRadius: 14,
                                  background: "rgba(15, 23, 42, 0.96)",
                                  border: "1px solid rgba(96, 165, 250, 0.25)",
                                }}
                              >
                                <div style={{ color: "#93c5fd", fontSize: 12, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                                  Flujo MQTT
                                </div>

                                <div style={{ marginBottom: 12 }}>
                                  <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                                    Solicitud del dispositivo
                                  </div>
                                  <div style={{ color: "#dbeafe", fontFamily: "monospace", fontSize: 13, wordBreak: "break-word", lineHeight: 1.45 }}>
                                    Topic: {buildMqttRequestTopic(protocolo)}
                                  </div>
                                  <div style={{ color: "#d1d5db", fontSize: 13, marginTop: 6 }}>
                                    Payload:
                                    <pre style={{ margin: "6px 0 0", whiteSpace: "pre-wrap", color: "#e5e7eb" }}>
{buildMqttRequestPayload()}
                                    </pre>
                                  </div>
                                </div>

                                <div>
                                  <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                                    Respuesta de la API
                                  </div>
                                  <div style={{ color: "#dbeafe", fontFamily: "monospace", fontSize: 13, wordBreak: "break-word", lineHeight: 1.45 }}>
                                    Topic: {buildMqttResponseTopic(protocolo)}
                                  </div>
                                  <div style={{ color: "#d1d5db", fontSize: 13, marginTop: 6 }}>
                                    Payload:
                                    <pre style={{ margin: "6px 0 0", whiteSpace: "pre-wrap", color: "#e5e7eb" }}>
{buildMqttResponsePayload(protocolo)}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            ) : null}

                            <div
                              style={{
                                padding: 12,
                                borderRadius: 14,
                                background: "rgba(255,255,255,0.04)",
                                border: "1px solid rgba(255,255,255,0.08)",
                              }}
                            >
                              <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                                Endpoint URL {selectedTransport ? `- ${selectedTransport}` : ""}
                              </div>
                              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                {endpointsVisibles.length > 0 ? (
                                  endpointsVisibles.map((endpoint) => (
                                    <div key={`${protocolo.codigo}-${endpoint.transport}-${endpoint.url}`}>
                                      <div style={{ color: "#b9d4ff", fontSize: 12, fontWeight: 800, marginBottom: 4 }}>
                                        {endpoint.transport}
                                      </div>
                                      <div
                                        style={{
                                          color: "#f8fafc",
                                          fontFamily: "monospace",
                                          fontSize: 13,
                                          wordBreak: "break-word",
                                          lineHeight: 1.45,
                                        }}
                                      >
                                        {endpoint.url}
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <div style={{ color: "#94a3b8", fontSize: 13, lineHeight: 1.5 }}>
                                    No hay endpoint definido para este transporte.
                                  </div>
                                )}
                              </div>
                            </div>

                            {hideObservations(protocolo) ? null : (
                              <div>
                                <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                                  Observaciones
                                </div>
                                <div style={{ color: "#cbd5e1", lineHeight: 1.65 }}>{protocolo.observaciones}</div>
                              </div>
                            )}
                          </Card.Body>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
